// API utilities for connecting frontend to backend services
import { post, get } from 'aws-amplify/api';
import { getDefaultPollyVoiceForLanguage } from '../constants/pollyStandardVoices';
import { parseWaitlistError, waitlistedError } from '../utils/waitlistError';

const normalizeSourcePayload = (source) => {
  if (!source) {
    return source;
  }

  const sourceLanguage = source.sourceLanguage || source.language || 'en';
  const translationLanguage = source.translationLanguage;  // Don't default to empty string
  const translationVoiceId = translationLanguage
    ? source.translationVoiceId || getDefaultPollyVoiceForLanguage(translationLanguage)
    : null;

  return {
    ...source,
    sourceLanguage,
    language: sourceLanguage,
    translationLanguage: translationLanguage || null,
    translationVoiceId: translationLanguage ? translationVoiceId : null
  };
};

const normalizeSourcesPayload = (sources) => {
  if (!Array.isArray(sources)) {
    return [];
  }
  return sources.map((source) => normalizeSourcePayload(source));
};

export const apiService = {
  // Save user news sources to database
  async saveUserSources(userId, sources) {
    try {
      
      const normalizedSources = normalizeSourcesPayload(sources);

      const requestBody = {
        userId: userId,
        sources: normalizedSources
      };
      
      const response = await post({
        apiName: 'userdata',
        path: '/news/sources',
        options: {
          body: requestBody
        }
      });
      
      // Handle the response similar to other API calls
      let result;
      try {
        if (response.response) {
          const rawResponse = await response.response;
          
          if (rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const decoder = new TextDecoder();
            let jsonString = '';
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              jsonString += decoder.decode(value, { stream: true });
            }
            
            result = JSON.parse(jsonString);
          } else {
            result = rawResponse.body;
          }
        } else if (response.body) {
          result = response.body;
        } else {
          result = response;
        }
      } catch (error) {
        console.error('❌ saveUserSources - Error accessing response:', error);
        throw new Error(`API response error: ${error.message}`);
      }
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save news sources');
      }
      
      return result;
      
    } catch (error) {
      console.error('Error saving sources:', error);
      throw error;
    }
  },

  // Get user news sources from database
  async getUserSources(userId) {
    try {
      
      const response = await get({
        apiName: 'userdata',
        // Append a cache-buster so a stale (e.g. previously-empty) GET response
        // can't be served from the browser/proxy cache after sources change.
        path: `/news/sources/get?userId=${encodeURIComponent(userId)}&_=${Date.now()}`
      });
      
      // Handle the response
      let result;
      try {
        if (response.response) {
          const rawResponse = await response.response;
          
          if (rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const decoder = new TextDecoder();
            let jsonString = '';
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              jsonString += decoder.decode(value, { stream: true });
            }
            
            result = JSON.parse(jsonString);
          } else {
            result = rawResponse.body;
          }
        } else if (response.body) {
          result = response.body;
        } else {
          result = response;
        }
      } catch (error) {
        console.error('❌ getUserSources - Error accessing response:', error);
        throw new Error(`API response error: ${error.message}`);
      }
      
      if (!result.success) {
        // If no sources found, return empty array instead of throwing error
        if (result.error && result.error.includes('not found')) {
          return [];
        }
        throw new Error(result.error || 'Failed to get news sources');
      }
      
  return normalizeSourcesPayload(result.sources || []);
      
    } catch (error) {
      console.error('Error getting sources:', error);
      // Return empty array for better UX if API fails
  return [];
    }
  },

  // Remove a news source
  async removeNewsSource(userId, sourceId) {
    try {
      
      const response = await post({
        apiName: 'userdata',
        path: `/news/sources/remove?userId=${encodeURIComponent(userId)}&sourceId=${encodeURIComponent(sourceId)}`,
        options: {
          body: {
            method: 'DELETE'
          }
        }
      });
      
      // Handle the response
      let result;
      try {
        if (response.response) {
          const rawResponse = await response.response;
          
          if (rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const decoder = new TextDecoder();
            let jsonString = '';
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              jsonString += decoder.decode(value, { stream: true });
            }
            
            result = JSON.parse(jsonString);
          } else {
            result = rawResponse.body;
          }
        } else if (response.body) {
          result = response.body;
        } else {
          result = response;
        }
      } catch (error) {
        console.error('❌ removeNewsSource - Error accessing response:', error);
        throw new Error(`API response error: ${error.message}`);
      }
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove news source');
      }
      
      return result;
      
    } catch (error) {
      console.error('Error removing news source:', error);
      throw error;
    }
  },

  // Download functionality
  downloadAudio(podcast) {
    try {
      
      const link = document.createElement('a');
      link.href = podcast.audioUrl;
      
      // Determine file extension based on URL or default to .mp3
      const urlParts = podcast.audioUrl.split('.');
      const extension = urlParts.length > 1 ? urlParts.pop() : 'mp3';
      
      link.download = `${podcast.title}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open in new window
      window.open(podcast.audioUrl, '_blank');
    }
  },

  // Extract news content using backend API
  async extractNewsContent(url, language = 'en') {
    try {
      
      
      const requestBody = {
        url: url,
        language: language
      };
      
      // Use Amplify API module which handles auth and CORS automatically
      const response = await post({
        apiName: 'userdata',
        path: '/news/parse',
        options: {
          body: requestBody
        }
      });
      
      
      // Try different ways to access the response data
      let result;
      try {
        if (response.response) {
          const rawResponse = await response.response;
          
          // Check if it's a ReadableStream that needs to be parsed
          if (rawResponse.body && rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const chunks = [];
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
            }
            
            // Combine chunks and decode
            const combinedArray = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
            let offset = 0;
            for (const chunk of chunks) {
              combinedArray.set(chunk, offset);
              offset += chunk.length;
            }
            
            const jsonString = new TextDecoder().decode(combinedArray);
            result = JSON.parse(jsonString);
          } else if (rawResponse.statusCode === 200 && rawResponse.body) {
            // Try to parse body directly if it's a string
            const bodyText = typeof rawResponse.body === 'string' ? rawResponse.body : JSON.stringify(rawResponse.body);
            result = JSON.parse(bodyText);
          } else {
            result = rawResponse;
          }
          
        } else if (response.body) {
          result = response.body;
        } else {
          result = response;
        }
      } catch (error) {
        console.error('❌ extractNewsContent - Error accessing response:', error);
        throw error;
      }
      
      
      if (!result.success || !result.content || result.content.length === 0) {
        console.error('❌ extractNewsContent - Invalid result:', {
          success: result.success,
          hasContent: !!result.content,
          contentLength: result.content?.length || 0,
          error: result.error
        });
        throw new Error(result.error || 'AWS news parsing returned no content');
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Error extracting news content:', error);
      throw error;
    }
  },

  // Save news article to database
  async saveNewsArticle(newsData, userId = 'anonymous', sourceOptions = {}) {
    try {
      
      
      const requestBody = {
        title: newsData.title,
        content: newsData.content,
        url: newsData.url,
        userId: userId,
        summary: newsData.summary,
        authors: newsData.authors,
        publishDate: newsData.publish_date,
        sourceLanguage: sourceOptions.sourceLanguage || newsData.sourceLanguage || 'en',
        translationLanguage: sourceOptions.translationLanguage || newsData.translationLanguage || null,
        translationVoiceId: sourceOptions.translationVoiceId || newsData.translationVoiceId || null
      };
      
      // Use Amplify API module which handles auth and CORS automatically
      const response = await post({
        apiName: 'userdata',
        path: '/news/save',
        options: {
          body: requestBody
        }
      });
      
      // Handle ReadableStream response just like in other functions
      let result;
      try {
        if (response.response) {
          const rawResponse = await response.response;
          
          // Check if it's a ReadableStream that needs to be parsed
          if (rawResponse.body && rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const chunks = [];
            
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
            }
            
            // Combine chunks and decode
            const combinedArray = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
            let offset = 0;
            for (const chunk of chunks) {
              combinedArray.set(chunk, offset);
              offset += chunk.length;
            }
            
            const jsonString = new TextDecoder().decode(combinedArray);
            result = JSON.parse(jsonString);
          } else if (rawResponse.statusCode === 200 && rawResponse.body) {
            // Try to parse body directly if it's a string
            const bodyText = typeof rawResponse.body === 'string' ? rawResponse.body : JSON.stringify(rawResponse.body);
            result = JSON.parse(bodyText);
          } else {
            result = rawResponse;
          }
          
        } else if (response.body) {
          result = response.body;
        } else {
          result = response;
        }
      } catch (error) {
        console.error('❌ saveNewsArticle - Error accessing response:', error);
        throw error;
      }
      
      if (!result.success) {
        throw new Error(result.error || 'AWS DynamoDB save returned failure');
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Error saving news article:', error);
      throw error;
    }
  },

  // Generate audio from text using AWS Polly only
  async generateAudioFromText(text, options = {}) {
    try {
      
      const {
        voice = 'Joanna',
        language = 'en-US',
        articleId = null,
        userId = 'anonymous'
      } = options;
      
      
      try {
        const requestBody = {
          text: text,
          voiceId: voice, // Use voiceId instead of voice
          language: language,
          articleId: articleId,
          userId: userId
        };
        
        // Use Amplify API module which handles auth and CORS automatically
        const response = await post({
          apiName: 'userdata',
          path: '/generate-audio',
          options: {
            body: requestBody
          }
        });
        
        // Handle ReadableStream response just like in other functions
        let result;
        try {
          if (response.response) {
            const rawResponse = await response.response;
            
            // Check if it's a ReadableStream that needs to be parsed
            if (rawResponse.body && rawResponse.body instanceof ReadableStream) {
              const reader = rawResponse.body.getReader();
              const chunks = [];
              
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
              }
              
              // Combine chunks and decode
              const combinedArray = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
              let offset = 0;
              for (const chunk of chunks) {
                combinedArray.set(chunk, offset);
                offset += chunk.length;
              }
              
              const jsonString = new TextDecoder().decode(combinedArray);
              result = JSON.parse(jsonString);
            } else if (rawResponse.statusCode === 200 && rawResponse.body) {
              // Try to parse body directly if it's a string
              const bodyText = typeof rawResponse.body === 'string' ? rawResponse.body : JSON.stringify(rawResponse.body);
              result = JSON.parse(bodyText);
            } else {
              result = rawResponse;
            }
            
          } else if (response.body) {
            result = response.body;
          } else {
            result = response;
          }
        } catch (error) {
          console.error('❌ generateAudioFromText - Error accessing response:', error);
          throw error;
        }
        
        if (!result.success) {
          throw new Error(result.error || 'AWS Polly audio generation failed');
        }
        
        // Check if this is an asynchronous job response
        if (result.jobId && result.status === 'processing') {
          
          // Poll for job completion
          return await this.waitForAudioGenerationJob(result.jobId, userId);
        }
        
        // Synchronous response - small text processed immediately
        if (!result.audioUrl) {
          throw new Error('AWS Polly did not return an audio URL');
        }
        
        return {
          success: true,
          audioUrl: result.audioUrl,
          duration: result.duration,
          chunks: result.chunks || 1,
          message: 'Audio generated successfully using AWS Polly'
        };
        
      } catch (awsError) {
        // Structured 403: {waitlisted:true} means the caller's free-tier entitlement is
        // 'waitlisted' (§9, free-tier-cap-enforcement plan) — this is a real, expected state,
        // not a broken backend, so it must NOT be wrapped in the generic Polly-unavailable
        // error below. Callers check `error.waitlisted` and open the pricing modal instead of
        // rendering this as a failure toast.
        const waitlistBody = await parseWaitlistError(awsError);
        if (waitlistBody) {
          throw waitlistedError(waitlistBody);
        }

        console.error('❌ AWS Polly endpoint failed:', awsError.message);

        // DO NOT FALLBACK - AWS ONLY as requested
        // Provide clear error message that AWS backend needs to be fixed
        console.error('🚨 AWS Polly backend unavailable:', awsError.message);

        // For debugging purposes, show the exact error but maintain AWS-only policy
        throw new Error(`🚨 AWS Polly Service Required: The backend endpoint '/generate-audio' is not accessible (${awsError.message}). This application requires AWS Polly for audio generation. Please check:\n\n1. AWS Lambda functions are deployed\n2. API Gateway endpoints are configured\n3. Backend synchronization is complete\n\nNo browser fallbacks are available - AWS Polly only as requested.`);
      }

    } catch (error) {
      // Preserve the waitlisted marker through this outer wrapper — otherwise the distinct
      // error type thrown above would be flattened back into a generic message string.
      if (error.waitlisted) {
        throw error;
      }
      console.error('❌ AWS Polly audio generation failed:', error);
      throw new Error(`Audio generation failed: ${error.message}`);
    }
  },

  // Poll for asynchronous audio generation job completion
  async waitForAudioGenerationJob(jobId, userId = 'anonymous', maxWaitTime = 900000) {
    const startTime = Date.now();
    const pollInterval = 2000; // Poll every 2 seconds
    
    while (Date.now() - startTime < maxWaitTime) {
      try {
        const statusResponse = await get({
          apiName: 'userdata',
          path: `/generate-audio/status?jobId=${jobId}&userId=${userId}`
        });
        
        let jobStatus;
        // Handle response parsing similar to other API calls
        if (statusResponse.response) {
          const rawResponse = await statusResponse.response;
          if (rawResponse.body && rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const chunks = [];
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
            }
            const combinedArray = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
            let offset = 0;
            for (const chunk of chunks) {
              combinedArray.set(chunk, offset);
              offset += chunk.length;
            }
            const jsonString = new TextDecoder().decode(combinedArray);
            jobStatus = JSON.parse(jsonString);
          } else {
            jobStatus = rawResponse.body ? JSON.parse(rawResponse.body) : rawResponse;
          }
        } else {
          jobStatus = statusResponse;
        }
        
        
        if (jobStatus.status === 'completed') {
          if (jobStatus.result && jobStatus.result.audioUrl) {
            return {
              success: true,
              audioUrl: jobStatus.result.audioUrl,
              duration: null,
              chunks: jobStatus.result.totalChunks || 1,
              combinedAudio: jobStatus.result.combinedAudio,
              message: `Audio generated successfully in background (${jobStatus.result.totalChunks} chunks)`
            };
          } else {
            throw new Error('Job completed but no audio URL returned');
          }
        } else if (jobStatus.status === 'failed') {
          const errorMsg = jobStatus.result?.error || 'Background job failed';
          throw new Error(`Background audio generation failed: ${errorMsg}`);
        }
        
        // Still processing - wait and poll again
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
      } catch (error) {
        console.error('❌ Error checking job status:', error);
        throw new Error(`Failed to check job status: ${error.message}`);
      }
    }
    
    // Timeout reached
    throw new Error(`Audio generation job timed out after ${maxWaitTime / 1000} seconds`);
  },

  // Translate text using AWS Translate
  async translateText(text, sourceLanguage, targetLanguage) {
    try {

      const requestBody = {
        text: text,
        sourceLanguage: sourceLanguage,
        targetLanguage: targetLanguage
      };

      const postResponse = await post({
        apiName: 'userdata',
        path: '/translate',
        options: {
          body: requestBody
        }
      });

      let result;
      try {
        if (postResponse.response) {
          const rawResponse = await postResponse.response;

          if (rawResponse.body instanceof ReadableStream) {
            const reader = rawResponse.body.getReader();
            const decoder = new TextDecoder();
            let jsonString = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              jsonString += decoder.decode(value, { stream: true });
            }

            result = JSON.parse(jsonString);
          } else {
            result = rawResponse.body;
          }
        } else if (postResponse.body) {
          result = postResponse.body;
        } else {
          result = postResponse;
        }
      } catch (error) {
        console.error('❌ translateText - Error accessing response:', error);
        throw new Error(`API response error: ${error.message}`);
      }

      
      if (!result.translatedText) {
        throw new Error(`No translatedText in response: ${JSON.stringify(result)}`);
      }
      

      return result.translatedText;

    } catch (error) {
      console.error('❌ Translation error:', error);
      throw new Error(`Failed to translate text: ${error.message}`);
    }
  },

  // Get the signed-in user's billing entitlement: {plan, status, currentPeriodEnd}
  async getEntitlement() {
    try {
      const result = await get({
        apiName: 'userdata',
        path: `/billing/entitlement?cb=${Date.now()}`  // cache-bust: a 304 reads as an error in amplify v6 (prod incident 2026-07-26)
      }).response;

      return await result.body.json();
    } catch (error) {
      console.error('❌ Error fetching entitlement:', error);
      throw error;
    }
  },

  // Get Paddle checkout config: {available:false} | {available:true, clientToken, priceMonth, priceYear, environment}
  async getBillingConfig() {
    try {
      const result = await get({
        apiName: 'userdata',
        path: `/billing/config?cb=${Date.now()}`
      }).response;

      return await result.body.json();
    } catch (error) {
      console.error('❌ Error fetching billing config:', error);
      throw error;
    }
  },

  // Create a Paddle customer-portal session (manage/cancel subscription).
  // Returns {url} on success; {reason} on 4xx/503 (payments_not_live | no_subscription).
  async getBillingPortalLink() {
    try {
      const result = await post({
        apiName: 'userdata',
        path: '/billing/portal-link'
      }).response;

      return await result.body.json();
    } catch (error) {
      // 4xx responses throw in amplify v6 — surface the body's reason when present.
      try {
        const body = await error?.response?.body?.json();
        if (body) return body;
      } catch (parseErr) { /* fall through */ }
      console.error('❌ Error creating billing portal link:', error);
      throw error;
    }
  },

  // The signed-in user's exact place in the waitlist queue: {position}. Cheap enough to call
  // when the waitlist UI is already showing (pricing modal, waitlist banner) — not on every
  // render.
  async getWaitlistPosition() {
    try {
      const result = await get({
        apiName: 'userdata',
        path: `/billing/waitlist-position?cb=${Date.now()}`
      }).response;

      return await result.body.json();
    } catch (error) {
      console.error('❌ Error fetching waitlist position:', error);
      throw error;
    }
  },

  // POST /user/heartbeat (§6/§9): stamps PROFILE.lastActiveAt and runs the server-side
  // offered→active claim (T6). Callers are responsible for throttling/fire-and-forget
  // semantics (see hooks/useHeartbeat.js) — this just makes the call.
  async sendHeartbeat() {
    const result = await post({
      apiName: 'userdata',
      path: '/user/heartbeat'
    }).response;

    return await result.body.json();
  }
};

export default apiService;
